import {isPaid, planOf, type Plan} from "home/gating";
import {isTrialPlan} from "account/trial";

/**
 * Which of the referrer's two rewards their copy names (kitecorp/transferify-ui#1693, owner decision
 * 2026-08-30).
 *
 * A referrer earns BOTH halves when a friend they invited first pays: `referral.trial-days` of Basic
 * entitlements (`ReferralFirstPurchaseRewardService`) and a month of Stripe balance credit
 * (`ReferralVestingService` + `ReferralRewardPricer`). Nothing here changes either grant — this module
 * decides only which one the sentence leads with, because the two are worth wildly different things to
 * different readers:
 *
 * - a FREE referrer notices 7 days of Basic and cannot spend a balance credit until they subscribe;
 * - a PAID referrer already has Basic and more, so the days are worth nothing to them and the credit is
 *   the only half worth stating.
 *
 * A referrer inside their own referral TRIAL counts as free here, and that is the case this programme
 * creates most of. `GetAccount` answers a trial window as `subscriptionActive: true` +
 * `subscriptionType: "BASIC"` (`account/trial.ts` sets out why), so `planOf` alone reports `basic` for
 * someone who pays nothing and cannot spend a balance credit. The api draws the same line on its own
 * money path — `ReferralVestingService` prices against `billingTierOf`, never `tierOf`, because an
 * entitlement nobody is paying for must not change what happens with money.
 *
 * The 2026-08-19 ruling ("credits… users don't care, they care about free trial of 7 days so that's
 * what we should tell them") is what keeps this from simply naming both everywhere; the 2026-08-30
 * ruling is what stops it from telling a Premium sender about Basic days. Together they are one rule
 * with two answers, which is why it is one function rather than a condition at each of the two call
 * sites — the account card and the success line must not be able to disagree about it.
 *
 * React-free by mandate (epic #26).
 */

/**
 * Which reward the referrer-facing sentence names.
 *
 * `"trial"` names the 7 days of Basic; `"credit"` names the month of credit. Deliberately NOT called
 * something like `"free"`/`"paid"`: the name says what the copy SAYS, so a reader of the catalog keys
 * can tell which sentence they are looking at without knowing this rule.
 */
export type ReferrerRewardStory = "trial" | "credit";

/**
 * The subscription facts the story depends on — structurally the same subset `planOf` reads from
 * `/account`, taken from it directly so a change to that contract is a compile error here rather than
 * a silently diverging duplicate.
 */
export type ReferrerRewardUser = Parameters<typeof planOf>[0] & {
	/** `GetAccount`\'s one signal that a TRIAL, not a subscription, is answering the plan fields. */
	trialExpiresAt: string | null | undefined;
};

/**
 * The story a plan earns, given whether a trial is what makes it look paid.
 *
 * `isPaid` rather than a tier comparison: "already has Basic" is exactly what makes the days worthless,
 * and it is true of every paid tier including any added later. `onTrial` is the correction that keeps
 * that reading honest for the one population where the plan field lies about payment.
 */
export function referrerRewardStoryOf(plan: Plan, onTrial: boolean): ReferrerRewardStory {
	return isPaid(plan) && !onTrial ? "credit" : "trial";
}

/**
 * The story a signed-in user earns, resolved end to end. THIS is what the React layer calls, so no
 * part of the rule — not even the plan lookup — sits in a hook or a view.
 *
 * `planOf` already collapses a lapsed or unrecognised subscription to `free`, which is what matters
 * here: someone whose subscription has ended has no Basic entitlements any more, so the days are worth
 * something to them again — and the credit they can no longer spend is not the half to lead with.
 * `isTrialPlan` handles the mirror case, an account that only looks paid.
 */
export function referrerRewardStoryForUser(user: ReferrerRewardUser): ReferrerRewardStory {
	return referrerRewardStoryOf(planOf(user), isTrialPlan(user.trialExpiresAt));
}
